import { NvSelectDropdown } from '@simwood/nv-react-components-v2';
import Fuse from 'fuse.js';
import mousetrap from 'mousetrap';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CallerIdPicker from '../components/CallerIdPicker';
import Contact from '../components/Contact';
import { getAudioDevices, testCanChangeAudioDevice, } from '../helpers/getAudioDevices';
import dtmfTones from '../helpers/tones';
import { handleEnterPress } from '../helpers/util';
import { cancelTransfer, sendDTMF, startCall, transferCall, } from '../reducers/calls';
import { switchExtension } from '../reducers/extension';
import { availableExtensionsSelector, combinedContactsSelector, } from '../selectors/contacts';
import styles from '../styles/dialler.scss';
import { BackspaceIcon, PhoneIcon, SearchIcon } from './icons';
const asterisk = String.fromCharCode(10033);
const noBreakSpace = String.fromCharCode(160);
const KEYS = [
    { display: '1', other: noBreakSpace },
    { display: '2', other: 'abc' },
    { display: '3', other: 'def' },
    { display: '4', other: 'ghi' },
    { display: '5', other: 'jkl' },
    { display: '6', other: 'mno' },
    { display: '7', other: 'pqrs' },
    { display: '8', other: 'tuv' },
    { display: '9', other: 'wxyz' },
    { display: asterisk, value: '*', other: noBreakSpace },
    { display: '0', other: '+' },
    { display: '#', other: noBreakSpace },
];
const ALLOWED_CHARS = KEYS.map((k) => k.value || k.display);
const isNumeric = (input) => {
    return input.split('').every((x) => ALLOWED_CHARS.includes(x));
};
const Dialler = (props) => {
    const dispatch = useDispatch();
    const contacts = useSelector(combinedContactsSelector);
    const call = useSelector((state) => state.calls.find((c) => c.active === true));
    const callId = call === null || call === void 0 ? void 0 : call.id;
    const tones = (call === null || call === void 0 ? void 0 : call.tones) || [];
    const availableExtensions = useSelector(availableExtensionsSelector);
    const extensionName = useSelector((state) => { var _a; return ((_a = state.extension.extension) === null || _a === void 0 ? void 0 : _a.name) || ''; });
    const extensionShortNumber = useSelector((state) => { var _a; return ((_a = state.extension.extension) === null || _a === void 0 ? void 0 : _a.shortNumber) || ''; });
    const selectedAudioInputDeviceId = useSelector((state) => state.client.selectedAudioInputDeviceId);
    const selectedAudioOutputDeviceId = useSelector((state) => state.client.selectedAudioOutputDeviceId);
    const selectedRingingOutputDeviceId = useSelector((state) => state.client.selectedRingingOutputDeviceId);
    const hasGrantedAudio = useSelector((state) => state.client.hasGrantedAudio);
    const availableExtensionsOptions = availableExtensions.map((x) => {
        return {
            value: x.id,
            label: `${x.shortNumber} - ${x.name}`,
        };
    });
    const keyPress = (id, tone) => {
        dispatch(sendDTMF({ id, tone }));
    };
    const callClick = (number, type, name, description, email) => {
        dispatch(startCall({ number, type, name, description, email }));
    };
    const transferClick = (callId, number) => {
        dispatch(transferCall({ id: callId, number }));
    };
    const transferCancelClick = (id) => {
        dispatch(cancelTransfer({ id }));
    };
    const doSwitchExtension = (id) => {
        dispatch(switchExtension(id));
    };
    const [inputValue, setInputValue] = useState('');
    const [focussedIdx, setFocussedIdx] = useState(null);
    const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState(null);
    const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState(null);
    const [canChangeAudioDevice, setCanChangeAudioDevice] = useState(false);
    const [showContacts, setShowContacts] = useState(false);
    const scrollContainerRef = useRef(null);
    const scrollListRef = useRef(null);
    const contactRef = useRef(null);
    const diallerInputRef = useRef(null);
    useEffect(() => {
        const focusInput = (e) => {
            var _a;
            e.preventDefault();
            // Focus the dialler input
            (_a = diallerInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        };
        mousetrap.bind(['mod+/'], focusInput);
        const canChangeAudioDevice = testCanChangeAudioDevice();
        getAudioDevices({
            selectedAudioInputDeviceId,
            selectedAudioOutputDeviceId,
            selectedRingingOutputDeviceId,
        })
            .then((devices) => {
            const { selectedAudioInputDevice, selectedAudioOutputDevice } = devices;
            setSelectedAudioInputDevice(selectedAudioInputDevice);
            setSelectedAudioOutputDevice(selectedAudioOutputDevice);
            setCanChangeAudioDevice(canChangeAudioDevice);
        })
            .catch(() => {
            setSelectedAudioInputDevice(null);
            setSelectedAudioOutputDevice(null);
            setCanChangeAudioDevice(canChangeAudioDevice);
        });
        return () => {
            mousetrap.unbind(['mod+/']);
        };
    }, []);
    const onKeyPress = (digit) => {
        // On dialler key press
        const { inCall } = props;
        if (inCall) {
            // FIXME
            const audio = dtmfTones[digit];
            audio.play();
            callId && keyPress(callId, digit);
            return;
        }
        // If not in call, append digit to inputValue and set it
        const newInputValue = inputValue + digit;
        setInputValue(newInputValue);
        setShowContacts(false);
    };
    const onInputChange = (value) => {
        // remove all whitespace on input and on paste
        value = value.replace(/ /g, '');
        // Force lower case input
        setInputValue(value);
        setFocussedIdx(null);
        setShowContacts(true);
    };
    const onBackspacePress = () => {
        // On backspace icon press
        // Remove last character from inputValue and set it
        const newInputValue = inputValue.slice(0, -1);
        setInputValue(newInputValue);
    };
    const focusNext = (contactCount) => {
        let newFocussedIdx;
        if (focussedIdx === null) {
            newFocussedIdx = 0;
        }
        else {
            newFocussedIdx = focussedIdx + 1;
            newFocussedIdx =
                newFocussedIdx > contactCount - 1 ? null : newFocussedIdx;
        }
        setFocussedIdx(newFocussedIdx);
    };
    const focusPrev = (contactCount) => {
        let newFocussedIdx;
        if (focussedIdx === null) {
            newFocussedIdx = contactCount - 1;
        }
        else {
            newFocussedIdx = focussedIdx - 1;
            newFocussedIdx = newFocussedIdx < 0 ? null : newFocussedIdx;
        }
        setFocussedIdx(newFocussedIdx);
    };
    const sendCall = (number) => {
        const { transfer } = props;
        // If there's no number, don't bother
        if (number.length === 0) {
            return;
        }
        // If we're transferring, just send it
        if (transfer && callId) {
            transferClick(callId, number);
            return;
        }
        // Lookup entered info to see if it matches any known contact
        // If it does, initiate the call with more detailed info
        const found = contacts === null || contacts === void 0 ? void 0 : contacts.some((c) => {
            // FIXME guard
            const contactNumber = c.shortNumber || c.phoneNumber;
            if (contactNumber === number) {
                callClick(contactNumber, c.type, c.name, contactNumber, c.email);
                return true;
            }
            return false;
        });
        // If not, just send a basic call
        if (!found) {
            callClick(number);
        }
    };
    const onInputKeyPress = (e, filteredContacts) => {
        const ESCAPE = 27;
        const contactCount = filteredContacts.length;
        if (e.keyCode === ESCAPE) {
            // On escape press, clear input
            setInputValue('');
            setFocussedIdx(null);
            setShowContacts(false);
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            focusPrev(contactCount);
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            focusNext(contactCount);
            return;
        }
        if (e.key === 'Enter') {
            // On enter press, send call if is numeric
            // If the dialler input value is numeric, send the call
            if (focussedIdx !== null) {
                const filteredContact = filteredContacts[focussedIdx];
                const number = filteredContact.shortNumber || filteredContact.phoneNumber;
                sendCall(number);
                setInputValue('');
                setFocussedIdx(null);
                setShowContacts(false);
            }
            else if (isNumeric(inputValue)) {
                sendCall(inputValue);
                setInputValue('');
                setFocussedIdx(null);
                setShowContacts(false);
            }
            else if (filteredContacts.length === 1) {
                const filteredContact = filteredContacts[0];
                const number = filteredContact.shortNumber || filteredContact.phoneNumber;
                sendCall(number);
                setInputValue('');
                setFocussedIdx(null);
                setShowContacts(false);
            }
        }
    };
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!contactRef.current) {
            // No filtered contacts exist, just return
            return;
        }
        const containerHeight = ((_a = scrollContainerRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0;
        const itemHeight = ((_b = contactRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
        const itemTop = (focussedIdx || 0) * itemHeight;
        const itemBottom = itemTop + itemHeight;
        const scrollTop = ((_c = scrollContainerRef.current) === null || _c === void 0 ? void 0 : _c.scrollTop) || 0;
        const scrollBottom = (scrollTop || 0) + (containerHeight || 0);
        const listHeight = (_d = scrollListRef.current) === null || _d === void 0 ? void 0 : _d.clientHeight;
        if (scrollContainerRef.current) {
            if (itemTop === 0) {
                // If the item is at the top of the container
                // Set the scroll to the top of the container
                scrollContainerRef.current.scrollTop = 0;
            }
            else if (itemBottom === listHeight) {
                // If the item is at the bottom of the container
                // Set the scroll to the bottom of the list
                scrollContainerRef.current.scrollTop = listHeight;
            }
            else if (itemTop < scrollTop) {
                // If the item is above the visible container
                // Set the scroll to the top of the item
                scrollContainerRef.current.scrollTop = itemTop;
            }
            else if (itemBottom > scrollBottom) {
                // If the item is below the visible container
                // Set the scroll so the item sits at the bottom of the container
                scrollContainerRef.current.scrollTop = itemBottom - containerHeight;
            }
        }
    }, [focussedIdx]);
    const getMatches = (contacts = [], searchPhrase) => {
        const fuse = new Fuse(contacts, {
            keys: [
                { name: 'name', weight: 1 },
                { name: 'shortNumber', weight: 0.8 },
                { name: 'phoneNumber', weight: 0.8 },
            ],
            threshold: 0.4,
        });
        // Remove () + and - from string
        return fuse.search(searchPhrase.replace(/[-+()\s]/g, ''));
    };
    const sendTransfer = (callId, inputValue) => {
        // If there's no number, don't bother
        if (inputValue.length === 0) {
            return;
        }
        transferClick(callId, inputValue);
    };
    const doCancelTransfer = (callId) => {
        setInputValue('');
        transferCancelClick(callId);
    };
    let filteredContacts = [];
    const normalizedInputValue = inputValue.toLowerCase().trim();
    // If the value entered is not numeric, search contacts for matches
    if (normalizedInputValue) {
        filteredContacts = getMatches(contacts, normalizedInputValue).map((match) => match.item);
    }
    const contactsVisible = showContacts &&
        (filteredContacts.length > 0 || normalizedInputValue.length > 0);
    const showBackspace = inputValue && !showContacts;
    const showSearch = !inputValue;
    const { inCall, transfer } = props;
    let bottomElement;
    if (inCall) {
        bottomElement = '';
    }
    else if (transfer) {
        const transferClicked = () => {
            if (callId) {
                sendTransfer(callId, inputValue);
            }
        };
        const cancelClicked = () => {
            if (callId) {
                doCancelTransfer(callId);
            }
        };
        bottomElement = (React.createElement("div", { className: styles.button_container },
            React.createElement("div", { className: styles.transfer_button, onClick: transferClicked, onKeyDown: handleEnterPress(transferClicked), role: 'button', tabIndex: 0 },
                React.createElement("span", { className: styles.button_label }, "Transfer")),
            React.createElement("div", { className: styles.cancel_button, onClick: cancelClicked, onKeyDown: handleEnterPress(cancelClicked), role: 'button', tabIndex: 0 },
                React.createElement("span", { className: styles.button_label }, "Cancel"))));
    }
    else {
        const callClicked = () => {
            sendCall(inputValue);
        };
        bottomElement = (React.createElement("div", { className: styles.button_container }, !hasGrantedAudio &&
            canChangeAudioDevice &&
            (!selectedAudioInputDevice || !selectedAudioOutputDevice) ? (React.createElement("div", { className: styles.button_disabled, title: 'No audio input/output device connected' },
            React.createElement(PhoneIcon, null))) : (React.createElement("div", { className: styles.button, onClick: callClicked, onKeyDown: handleEnterPress(callClicked), role: 'button', tabIndex: 0 },
            React.createElement(PhoneIcon, null)))));
    }
    return (React.createElement("div", { className: styles.container },
        React.createElement("div", { className: styles.top_container },
            React.createElement("div", { className: styles.input_container, onClick: (e) => {
                    var _a, _b;
                    if (!inCall && ((_a = e.target) === null || _a === void 0 ? void 0 : _a.id) !== 'backspace') {
                        (_b = diallerInputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                } }, inCall ? (React.createElement("div", { className: styles.display }, tones.slice().reverse().join(''))) : (React.createElement(React.Fragment, null,
                showSearch && React.createElement(SearchIcon, { className: styles.search_icon }),
                React.createElement("input", { className: styles.input, value: inputValue, onChange: () => {
                        var _a;
                        onInputChange(((_a = diallerInputRef.current) === null || _a === void 0 ? void 0 : _a.value) || '');
                    }, onKeyDown: (e) => onInputKeyPress(e, filteredContacts), ref: diallerInputRef, spellCheck: 'false', disabled: !hasGrantedAudio }),
                showBackspace && (React.createElement(BackspaceIcon, { className: styles.backspace, onClick: () => onBackspacePress() }))))),
            React.createElement("div", { className: styles.extension_container },
                React.createElement(NvSelectDropdown, { type: 'inline', color: {
                        text: 'var(--primary)',
                    }, isClearable: false, isSearchable: false, className: styles.user_dropdown, options: availableExtensionsOptions, defaultValue: `${extensionShortNumber} - ${extensionName}`, onChange: (selected) => {
                        doSwitchExtension(selected[0].value);
                    }, menuAlign: 'right', selectStyles: {
                        singleValue: (base) => (Object.assign(Object.assign({}, base), { color: 'var(--white-color)' })),
                        menu: (base) => (Object.assign(Object.assign({}, base), { width: 'max-content' })),
                        dropdownIndicator: (base) => (Object.assign(Object.assign({}, base), { color: 'var(--white-color)', '&:hover': {
                                color: 'var(--white-color)',
                            } })),
                    } }))),
        React.createElement("div", { className: contactsVisible
                ? styles.content_container_contacts
                : styles.content_container },
            React.createElement("div", { className: styles.contacts_container, ref: scrollContainerRef },
                React.createElement("ul", { className: styles.contacts_list, ref: scrollListRef }, filteredContacts.length > 0 ? (filteredContacts.map((contact, i) => {
                    const number = contact.shortNumber || contact.phoneNumber;
                    const focussed = focussedIdx === i;
                    return (React.createElement("div", { key: i, ref: contactRef },
                        React.createElement(Contact, { name: contact.name, description: number, email: contact.email, id: contact.id, number: number, type: contact.type, status: contact.status, focussed: focussed })));
                })) : (React.createElement("li", { className: styles.no_contacts }, "No matching contacts found")))),
            React.createElement("div", { className: styles.key_container }, KEYS.map((k, i) => (React.createElement("div", { className: `${styles.key} ${!hasGrantedAudio && styles.disabled}`, onClick: () => onKeyPress(k.value || k.display), key: i, onKeyDown: handleEnterPress(() => onKeyPress(k.value || k.display)), role: 'button', tabIndex: 0 },
                React.createElement("span", { className: styles.key_digit }, k.display),
                React.createElement("span", { className: styles.key_other }, k.other.toUpperCase())))))),
        React.createElement(CallerIdPicker, null),
        bottomElement));
};
export default Dialler;
